import platform from '@play-co/gcinstant';
import { isFirstEntryOfDay, removeSplashScreen } from 'src/lib/platformUtils';
import { openPopupPromise } from 'src/lib/popups/popupOpenClose';
import sounds from 'src/lib/sounds';
import { Feature } from 'src/replicant/analytics';
import { getLaunchScene, isSceneEntered } from 'src/lib/stateUtils';
import { Social } from 'src/replicant/social';
import { assertNever } from 'src/replicant/utils';
import StateObserver from 'src/StateObserver';
import { PopupID } from '../components/popups/popupRegistry';
import { hasGiftsMessages, isGiftsFeatureUnlocked } from '../gifts/helpers';
import { levelUpSequence } from '../level/level.sequence';
import { socialSequences } from '../social/social.sequence';
import { actionBlock, createLaunchSequence } from './LaunchSequence';
import { Position } from '../position/PositionModel';
import { getPet } from '../pet';
import { getTutorialController } from '../tutorial/TutorialController';
import {
  startDuoResponseSequence,
  startDuoInvitationReceivedSequence,
} from '../duo/duo.sequence';
import {
  guessInstalledNativeAppPlatform,
  isNativePromoAvailable,
} from '../native/native.util';
import { isPlatformGuest } from 'src/lib/utils';
import {
  startInstallAppRewardSequence,
  startInstallNativeSequence,
} from '../native/native.sequence';
import { createPersistentEmitter } from 'src/lib/Emitter';
import PopupMonitor from '../logic/PopupMonitor';
import features from 'src/replicant/ruleset/features';
import { getDynamicTestBucket } from 'src/replicant/getters/ab';
import ab from 'src/replicant/ruleset/ab';
import { AB } from 'src/lib/AB';
import { duration } from 'src/replicant/utils/duration';

export const defaultLaunchSequence = createLaunchSequence([
  /**
   * Handle crossplay
   */
  actionBlock({
    skip: () =>
      !isNativePromoAvailable() || guessInstalledNativeAppPlatform() === null,
    actions: [
      async () => {
        await openPopupPromise('popupLoginCrossplay', { origin: 'launch' });

        // Player didn't redirect to native so continue with next action.
        return false;
      },
    ],
  }),

  /**
   * Load the initial scene.
   */
  actionBlock({
    actions: [
      async () => {
        const scene = getLaunchScene();
        const petController = getPet();

        let petPosition: Position;
        if (scene === 'bedroom') {
          petPosition = { scene, location: 'bed' };
        } else {
          petPosition = { scene, location: 'c' };
        }
        await petController.moveTo(petPosition, 0);

        // Remove splash screen, with a small delay to avoid black screen flash
        setTimeout(() => removeSplashScreen(), 500);

        // Start bgm once the scene is entered.
        sounds.music.playBackgroundMusic('bgm');
        const { settings } = StateObserver.getState().user;
        sounds.musicMuted = !settings.music;
        sounds.sfxMuted = !settings.sound;

        // Continue with next action.
        return false;
      },
    ],
  }),

  /**
   * Processes deep links.
   * Note: this is an action, returning true will end the sequence.
   */
  async () => {
    const { type } = platform.entryData.$deepLink ?? {};

    // No deep link, bail out.
    if (!type) {
      return false;
    }

    // Execute action based on type.
    switch (type) {
      // Open popup.
      case 'popup': {
        // Only whitelisted popups can be opened.
        const whiteListedPopups: PopupID[] = ['popupSettings'];

        // Get popup data.
        const { id, args } = platform.entryData.$deepLink;

        // Not allowed to open this popup, bail out.
        if (!whiteListedPopups.includes(id as PopupID)) return false;

        // Open popup.
        await openPopupPromise('popupSettings', args ?? {});

        // Break out of sequence.
        return true;
      }
      // Should never happen.
      default:
        assertNever(type);
    }
  },

  /**
   * Trigger FTUX if needed.
   * Note: this is an action, returning true will end the sequence.
   */
  () => getTutorialController().handleEvent('gameStarted'),

  /**
   * Reward for native app installation
   */
  () => startInstallAppRewardSequence(),

  /**
   * Show duo response received sequence.
   */
  async () => {
    // Duo not enabled, bail.
    if (!features(StateObserver.getState().user).duo) return false;

    // Show any pending duo responses.
    const result = await startDuoResponseSequence();

    // Attach a listener to trigger debuts in realtime.
    createPersistentEmitter(
      ({ user, care, duo }) =>
        duo.canDebut && // Check if debut is not blocked.
        !!user.duo.debut && // Debut is available.
        !care.currentCareAction && // No care action is active.
        isSceneEntered('livingRoom') && // Player is in the living room.
        PopupMonitor.getOpenPopups().length === 0, // No popups are open.
    ).addListener(async (shouldDebut) => {
      if (!shouldDebut) return;
      void startDuoResponseSequence();
    });

    return result;
  },

  /**
   * Show energy gift popup if there are unclaimed gifts.
   */
  actionBlock({
    skip: ({ user }) =>
      !isGiftsFeatureUnlocked(user) ||
      (!isFirstEntryOfDay() && !hasGiftsMessages(user)),
    actions: [
      async () => {
        await openPopupPromise('popupGiftsList', {});

        return true;
      },
    ],
  }),

  /**
   * Show friend referral dialog if feature is available and is the first entry of the day.
   */
  actionBlock({
    skip: ({ user }) =>
      isPlatformGuest(platform) ||
      !Social.getters(user).isFriendInviteAvailable() ||
      !isFirstEntryOfDay(),
    actions: socialSequences.invite(Feature.INVITE.INVITE_LAUNCH),
  }),

  /**
   * Show referral rewards if available
   */
  actionBlock({
    skip: () => {
      const { user } = StateObserver.getState();
      return Social.getters(user).getUnclaimedReferralRewardCount() === 0;
    },
    actions: socialSequences.referralRewards(
      'invite_share_gems_reward_success',
    ),
  }),

  /**
   * Show app install on launch if:
   * - Native promo available
   * - App not installed
   * - Is first entry of day
   */
  actionBlock({
    enabled: ({ user }) =>
      isNativePromoAvailable() &&
      guessInstalledNativeAppPlatform() === null &&
      isFirstEntryOfDay(),
    actions: [
      async () => {
        await startInstallNativeSequence('launch');

        return false;
      },
    ],
  }),

  /**
   * Show level up reward if available.
   */
  actionBlock({
    skip: ({ user: { level } }) => level.current - level.lastLevelReward <= 0,
    actions: [
      async () => {
        await levelUpSequence();

        return true;
      },
    ],
  }),

  /**
   * Show duo invitation received sequence.
   */
  actionBlock({
    skip: ({ user }) => !features(user).duo,
    actions: [
      async () => {
        await startDuoInvitationReceivedSequence();

        return true;
      },
    ],
  }),

  /**
   * Show LINE login popup to guest mode players
   */
  actionBlock({
    skip: () => !isPlatformGuest(platform),
    actions: [
      async () => {
        await openPopupPromise('popupNativeLineGuestLogin', {
          origin: 'sequence',
        });

        // Continue with next action.
        return false;
      },
    ],
  }),
]);
